import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { LandingPage } from '../../features/landing';
import { tryRefresh } from '../api';
import { AppShell } from '../layout/AppShell';
import { setLocale } from '../../i18n';
import { Spinner } from '../ui';
import { rememberedUser, useSession } from './session-store';

let bootstrapping: Promise<void> | null = null;
/** Tests only: forget the per-page-load bootstrap. */
export const resetBootstrap = () => {
  bootstrapping = null;
};

/** Restores the session from the refresh cookie once per page load. */
function useBootstrapSession() {
  const status = useSession((s) => s.status);
  useEffect(() => {
    if (status !== 'unknown') return;
    bootstrapping ??= tryRefresh().then(({ session, offline }) => {
      const remembered = rememberedUser();
      if (session) {
        useSession.getState().signIn(session.accessToken, session.user);
        if (session.user.locale === 'ar' || session.user.locale === 'en')
          setLocale(session.user.locale);
      } else if (offline && remembered) {
        // Started without a connection: open read-only from the offline cache. The first
        // request once online gets a 401 and the client refreshes the session then.
        useSession.getState().signIn(null, remembered);
      } else {
        useSession.getState().signOut();
      }
    });
  }, [status]);
  return status;
}

/** UX-only guard: the API enforces every authorization decision. */
export function RequireAuth() {
  const status = useBootstrapSession();
  const location = useLocation();
  if (status === 'unknown') return <Spinner />;
  if (status === 'anonymous') {
    const next = location.pathname + location.search;
    return (
      <Navigate to={next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />
    );
  }
  return <Outlet />;
}

/**
 * The home route: visitors who are not signed in see the landing page at `/`; every other
 * private path still sends them to the login page. Signed-in users get the app.
 */
export function HomeGate() {
  const status = useBootstrapSession();
  const location = useLocation();
  if (status === 'unknown') return <Spinner />;
  if (status === 'anonymous') {
    if (location.pathname === '/') return <LandingPage />;
    const next = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <AppShell />;
}

/** Login/register pages bounce signed-in users back into the app. */
export function PublicOnly() {
  const status = useBootstrapSession();
  const location = useLocation();
  if (status === 'unknown') return <Spinner />;
  if (status === 'authenticated') {
    const next = new URLSearchParams(location.search).get('next');
    return <Navigate to={safeNext(next)} replace />;
  }
  return <Outlet />;
}

/** Pages reachable either way (verify email, invitation preview). */
export function SessionAware() {
  const status = useBootstrapSession();
  if (status === 'unknown') return <Spinner />;
  return <Outlet />;
}

/**
 * Only same-app paths are allowed as redirect targets (no open redirect). Browsers read `\`
 * as `/` and drop tabs/newlines, so `/\evil.com` or `/\t/evil.com` would mean `//evil.com`;
 * React Router then falls back to a full `location.assign` when pushState refuses it.
 */
export function safeNext(next: string | null): string {
  if (!next || !next.startsWith('/') || /^\/\//.test(next)) return '/';
  // eslint-disable-next-line no-control-regex
  return /[\\\u0000-\u001f\u007f]/.test(next) ? '/' : next;
}
